import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import useCountDown from 'react-countdown-hook';

import { TimetableRuleState } from '@chirp/access-verification';

import { BottomDrawerModal, Button, ContentPreloader, Icon, Progress } from '../';
import {
  AccessPointFragment,
  usegetAccessPointQuery,
  useunlockAccessPointMutation,
} from '../../apollo';
import { colors } from '../../assets/styles';
import {
  getAvailabilityState,
  getLockdownEndDateString,
  isDateAllowedOnTimetable,
  renderParagraph,
} from '../../utils';
import { closeAnimationDurationInMS } from '../BottomDrawerModal/styles';
import styles from './styles';

enum LockState {
  LOCKED = 'locked',        // Entry is currently in a locked state
  UNLOCKING = 'unlocking',  // Entry is currently locked but an API call has been made to unlock the entry point
  UNLOCKED = 'unlocked',    // Entry is currently in an unlocked state after a successful API call
  OPEN = 'open',            // Entry is currently open due to an UNLOCKED availability schedule
}

interface TapToUnlockDrawerProps {
  accessPointId: AccessPointFragment['accessPointId'];
  expiresAt: string | null;
  onFetchedAccessPoint: (accessPoint: AccessPointFragment) => void;
}

const TapToUnlockDrawer: React.FC<TapToUnlockDrawerProps> = (props) => {
  const { accessPointId, expiresAt, onFetchedAccessPoint } = props;
  const [savedTimeout, setSavedTimeout] = useState<NodeJS.Timeout>();
  const [countdown, setCountdown] = useState(0);
  const [startCountdown, setStartCountdown] = useState(false);
  const [timeLeft, { start, reset }] = useCountDown(countdown, 100);
  const [hasError, setHasError] = useState(false);

  const [show, setShow] = useState<boolean>(false);

  const [lockState, setLockState] = useState<{
    lockState: LockState
    description?: string | JSX.Element,
  }>({
    lockState: LockState.LOCKED,
    description: '',
  });

  const [unlockAccessPointMutation] = useunlockAccessPointMutation();
  const [accessPoint, setAccessPoint] = useState<AccessPointFragment>();
  const [accessPointState, setAccessPointState] = useState<TimetableRuleState>(TimetableRuleState.LOCKDOWN);

  usegetAccessPointQuery({
    skip: !accessPointId,
    variables: { accessPointId },
    onCompleted: async ({ accessPoint: loadedAccessPoint }) => {
      try {
        const { timetable } = loadedAccessPoint;
        const { timezone } = loadedAccessPoint.property;

        const accessTime = new Date();

        if (!moment(accessTime).isBefore(expiresAt)) {
          // Simulate API error when access is expired
          throw new Error('You do not have access to this entry point');
        }

        const currentTimetableState = getAvailabilityState(new Date(), timetable, timezone);
        setAccessPoint(accessPoint);

        // Access point is currently locked down and not available to anyone
        if (currentTimetableState === TimetableRuleState.LOCKDOWN) {
          throw new Error(getLockdownEndDateString(new Date(), timetable, timezone));
        }

        // Access point is currently UNLOCKED and does not require access request
        if (currentTimetableState === TimetableRuleState.UNLOCKED) {
          setAccessPointState(TimetableRuleState.UNLOCKED);
          setShow(true);
          setLockState({
            lockState: LockState.OPEN,
            description: 'This entry point is currently open and will remain unlocked',
          });
        } else if (!isDateAllowedOnTimetable(new Date(), timetable, timezone)) {
          setAccessPointState(TimetableRuleState.LOCKDOWN);
          throw new Error('This entry point is closed right now.');
        } else {
          setAccessPointState(TimetableRuleState.CONTROLLED);
          setShow(true);
          setLockState({
            lockState: LockState.UNLOCKING,
            description: <ContentPreloader background="transparent" />,
          });

          onFetchedAccessPoint(loadedAccessPoint);
          await unlockAccessPointMutation({
            variables: { accessPointId },
          });
          setLockState({ lockState: LockState.UNLOCKED });
          closeDrawer(loadedAccessPoint.accessTimeout);
        }
      } catch (err: any) {
        setShow(true);
        setHasError(true);
        setLockState({
          lockState: LockState.LOCKED,
          description: err?.message ||
            'There was an error unlocking that access point. Please try again or contact support.',
        });
      }
    },
    onError: (err) => {
      setShow(true);
      setHasError(true);
      setLockState({
        lockState: LockState.LOCKED,
        description: err.message ||
          'There was an error unlocking that access point. Please try again or contact support.',
      });
      closeDrawer(4000);
    },
  });

  const closeDrawer = async (timeout: number) => {
    setCountdown(timeout);
    setStartCountdown(true);

    // After first timeout set lock back to "Locked" state
    setSavedTimeout(
      setTimeout(() => {
        setLockState((prevState) => ({
          lockState: prevState.lockState === LockState.OPEN ? LockState.OPEN : LockState.LOCKED,
        }));
        // After second timeout run "onFinish()" and close drawer
        setSavedTimeout(
          setTimeout(() => {
            setLockState((prevState) => ({
              lockState: prevState.lockState === LockState.OPEN ? LockState.OPEN : LockState.LOCKED,
            }));
            setCountdown(0);
            setStartCountdown(false);
          }, 1000 + closeAnimationDurationInMS),
        );
      }, timeout),
    );
  };

  // Cleaup for timeout
  useEffect(() => {
    return () => {
      if (savedTimeout) {
        clearInterval(savedTimeout);
      }
    };
  }, [savedTimeout]);

  // useEffect for progress bar
  useEffect(() => {
    reset();
    if (startCountdown) {
      start(countdown);
    }
  }, [startCountdown, countdown, reset, start]);

  const completionPercent = !startCountdown ? 0 : (countdown - timeLeft) / countdown * 105;

  return (
    <BottomDrawerModal
      className={styles.container}
      show={show}
    >
      {accessPointState === TimetableRuleState.CONTROLLED && <Progress progress={completionPercent} />}
      <div className={styles.content}>
        <div className={styles.iconContainer}>
          <div className={styles.icon}>
            {lockState.lockState === LockState.UNLOCKED || lockState.lockState === LockState.OPEN
              ? <Icon.Unlock color={colors.black} />
              : <Icon.Lock color={colors.black} />
            }
          </div>
        </div>
        <div className={styles.text}>
          <div className={styles.header}>
            {lockState.lockState}
          </div>
          <div className={styles.description}>
            {lockState.lockState !== LockState.UNLOCKED
              ? typeof lockState?.description === 'string'
                ? renderParagraph(lockState.description)
                : lockState.description
              : `Locking in ${Math.round((timeLeft / 1000) % 60)} seconds...`
            }
          </div>
        </div>
        {accessPointState === TimetableRuleState.CONTROLLED && !hasError &&
          <Button
            disabled={lockState.lockState !== LockState.LOCKED}
            className={styles.button}
            type="primary"
            size="large"
            expand
            onClick={async () => {
              if (!accessPoint) return;
              if (accessPointState !== TimetableRuleState.CONTROLLED) return;

              setShow(true);
              setLockState({
                lockState: LockState.UNLOCKING,
                description: <ContentPreloader background="transparent" />,
              });
              await unlockAccessPointMutation({
                variables: {
                  accessPointId: accessPoint.accessPointId,
                },
              });
              setLockState({ lockState: LockState.UNLOCKED });
              closeDrawer(accessPoint.accessTimeout);
            }}
          >
            Unlock
          </Button>
        }
      </div>
    </BottomDrawerModal>
  );
};

export default TapToUnlockDrawer;
